import React, { useContext, useMemo } from 'react';

import useSelectedEntity from './useSelectedEntity';
import useSelectedEntityFilter from './useSelectedEntityFilter';
import EntityDetailsContext from '../EntityDetailsContext';
import useTabs from './useTabs';
import useEntityOptions from './useEntityOptions';
import { MappedUserContentSettingsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/MappedUserContentSettingsProvider';
import Loading from '../../../components/Loading';

const SelectedEntityProvider = ({
  children,
  entity,
  entityApp,
}: {
  children: JSX.Element | JSX.Element[];
  entityApp: EntityDetails.App;
  entity: EntityDetails.Entity;
}) => {
  const { selectedMappedUser, isMappedUserForCurrentEntity } = useContext(
    MappedUserContentSettingsContext,
  );
  const { selectedEntity, onEntitySelected } = useSelectedEntity(entity);
  const selectedEntityFilter = useSelectedEntityFilter(selectedEntity, entity);
  const { tabs, selectedTab } = useTabs({
    app: entityApp,
  });
  const {
    entityOptions,
    entitySearchText,
    onEntitySearchTextChanged,
    isLoadingEntityOptions,
  } = useEntityOptions({
    selectedEntity,
    onEntitySelected,
  });

  const selectedEntityLabel = useMemo(() => {
    if (!isMappedUserForCurrentEntity) {
      return selectedEntity ? selectedEntity.fieldValue : '?';
    }

    if (selectedMappedUser) {
      return selectedMappedUser.user.displayName;
    }

    return 'All Users';
  }, [isMappedUserForCurrentEntity, selectedEntity, selectedMappedUser]);

  if (!entityApp || !selectedEntityFilter) {
    return <Loading />;
  }

  const { entityFilter, entityFilterAsFilterPlates } = selectedEntityFilter;

  return (
    <EntityDetailsContext.Provider
      value={{
        app: entityApp,
        entityFilter,
        entityFilterAsFilterPlates,
        selectedEntity,
        selectedEntityLabel,
        onEntitySelected,
        tabs,
        selectedTab,
        entityOptions,
        entitySearchText,
        onEntitySearchTextChanged,
        isLoadingEntityOptions,
      }}
    >
      {children}
    </EntityDetailsContext.Provider>
  );
};

export default SelectedEntityProvider;
